import {
  useGetLandingUrlsForSelectQuery,
  useGetTagsForSelectQuery
} from '@/app/KeywordTracker/api/keywordsApi';
import { useState } from 'react';
import { Accordion, Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import OptionLayoutLanguageTracker from '@/app/common/components/OptionsLayout/OptionLayoutLanguageTracker';
import OptionLayoutLandingUrls from '@/app/common/components/OptionsLayout/OptionLayoutLandingUrls';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useGetGroupsForSelectQuery } from '@/app/KeywordTracker/api/groupsApi';
import type { AddKeywordsFormValues, SetFieldValue } from './types';

interface AdvancedAddKeywordsSettingProps {
  domain_slug: string;
  values: AddKeywordsFormValues;
  setFieldValue: SetFieldValue;
}

const AdvancedAddKeywordsSetting = ({
  domain_slug,
  values,
  setFieldValue
}: AdvancedAddKeywordsSettingProps) => {
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  return (
    <div id="advanced-features" className="my-4">
      <style>
        {`
    #advanced-features .accordion-button {background-color: transparent !important; font-size:13.3333px; padding:0px; box-shadow:none; }
    #advanced-features .accordion-item {background-color: transparent !important; border:none;}
    #advanced-features .accordion-button:focus {box-shadow:none;}
    #advanced-features .accordion-button::after { background-size: 0.8rem; width: 0.8rem; height: 0.8rem;}
    `}
      </style>
      <Accordion
        defaultActiveKey={[]}
        onSelect={isOpen => setIsSettingOpen(!!isOpen)}
      >
        <Accordion.Item on eventKey="1">
          <Accordion.Header style={{ width: 115 }}>
            تنظیمات پیشرفته
          </Accordion.Header>
          <Accordion.Body className="py-1 pb-3 px-0 bg-transparent">
            {isSettingOpen && (
              <AccordionData
                values={values}
                setFieldValue={setFieldValue}
                domain_slug={domain_slug}
              />
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default AdvancedAddKeywordsSetting;

interface AccordionDataProps {
  domain_slug: string;
  values: Record<string, unknown>;
  setFieldValue: SetFieldValue;
}

const AccordionData = ({ domain_slug, values, setFieldValue }: AccordionDataProps) => {
  const { data: userInfo } = useGetUserInfoQuery();
  const { data: tags, isFetching: isFetchingTags } =
    useGetTagsForSelectQuery(domain_slug);
  const { data: groups, isFetching: isFetchingGroups } =
    useGetGroupsForSelectQuery(domain_slug);
  const { data: landings, isFetching: isFetchingLandings } =
    useGetLandingUrlsForSelectQuery(domain_slug);
  return (
    <>
      <Row className="my-2 g-3">
        <Col>
          {!isFetchingTags && tags?.data ? (
            <Select
              menuPlacement="top"
              classNamePrefix="react-select"
              menuShouldScrollIntoView={true}
              maxMenuHeight={200}
              closeMenuOnSelect={false}
              placeholder="انتخاب برچسب"
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  direction: 'rtl !important',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important',
                  borderRadius: '5px 5px 5px 5px !important',
                  width: '100% !important',
                  cursor: 'pointer'
                }),
                input: (baseStyles, state) => ({
                  ...baseStyles,
                  direction: 'ltr !important',
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important'
                })
              }}
              components={{
                Option: OptionLayoutLanguageTracker,
                SingleValue: SingleValueFlag
              }}
              onChange={e => {
                setFieldValue('tag_ids', e);
              }}
              isMulti
              value={values.tag_ids}
              isSearchable={true}
              noOptionsMessage={() => 'یافت نشد'}
              options={tags?.data}
            />
          ) : (
            <TextLoading width={100} height={37} />
          )}
        </Col>
        <Col>
          {!isFetchingGroups && groups?.data ? (
            <Select
              menuPlacement="top"
              isClearable={true}
              maxMenuHeight={200}
              menuShouldScrollIntoView={true}
              closeMenuOnSelect={true}
              isSearchable={true}
              placeholder="انتخاب دسته"
              onChange={e => setFieldValue('group_id', e)}
              options={groups?.data}
              classNamePrefix="react-select"
              value={values.group_id}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  paddingLeft: '0px',
                  paddingRight: '10px',
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important',
                  cursor: 'pointer',
                  width: '100% !important',
                  borderRadius: '5px 5px 5px 5px !important'
                }),
                input: (baseStyles, state) => ({
                  ...baseStyles,
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important'
                })
              }}
              components={{
                Option: OptionLayoutLanguageTracker,
                SingleValue: SingleValueFlag
              }}
            />
          ) : (
            <TextLoading width={100} height={37} />
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          {!isFetchingLandings && landings?.data ? (
            <Select
              classNamePrefix="react-select"
              menuPlacement="top"
              menuShouldScrollIntoView={true}
              closeMenuOnSelect={true}
              placeholder="انتخاب صفحه هدف برای کلمات کلیدی"
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  direction: 'ltr !important',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important',
                  borderRadius: '5px 5px 5px 5px !important',
                  width: '100% !important',
                  cursor: 'pointer'
                }),
                input: (baseStyles, state) => ({
                  ...baseStyles,
                  direction: 'ltr !important',
                  color: 'var(--falcon-emphasise) !important',
                  boxShadow: 'none !important'
                })
              }}
              components={{ Option: OptionLayoutLandingUrls }}
              onChange={e => {
                setFieldValue('landing_url_id', e);
              }}
              value={values.landing_url_id}
              isSearchable={true}
              noOptionsMessage={() => 'یافت نشد'}
              options={[
                {
                  label: 'صفحه پیدا شده در گوگل',
                  value: null
                },
                ...landings.data
              ]}
            />
          ) : (
            <TextLoading width={100} height={37} />
          )}
        </Col>
      </Row>
    </>
  );
};
